/**
 * `/casos/:id/orientacoes` (E158.F3, GUS-191; ADR-0108 §2.6): a minuta das orientações para o
 * jurídico, na área da equipe — a mesma leitura que o portal mostra, a situação da liberação, o
 * PDF da minuta e "Liberar de novo" quando o laudo da versão foi assinado e as orientações não
 * saíram (CA07). O link de entrada sai da página do resultado.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { BaixarOrientacoes, OrientacoesReservadas } from '@/components/Orientacoes';
import { formatarDataHora } from '@/components/Portal/Caso/rotulos';
import { ProximoPasso } from '@/components/ProximoPasso/ProximoPasso';
import { Button } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import { useApiClient } from '@/lib/api';
import {
  OrientacoesError,
  baixarMinutaDasOrientacoes,
  lerOrientacoesDoCaso,
  liberarAsOrientacoes,
  type MotivoDaFalha,
  type OrientacoesDoCasoData,
} from '@/lib/orientacoes';

const t = ptBR.orientacoes;
const te = t.equipe;

function motivoDe(err: unknown): MotivoDaFalha {
  return err instanceof OrientacoesError ? err.motivo : 'indisponivel';
}

/** O motivo em texto de tela; o código conhecido ou não nunca aparece (E158.F3.C01). */
function motivoNaTela(motivo: string | null): string {
  return (motivo !== null && te.motivos[motivo]) || te.motivoDesconhecido;
}

function Situacao({ dados }: { dados: OrientacoesDoCasoData }) {
  const texto = te.situacao[dados.situacao].replace('{motivo}', motivoNaTela(dados.motivo));
  const final = dados.final;
  return (
    <div
      className="border border-border bg-surface p-sc-4"
      data-testid="orientacoes-situacao"
      data-motivo={dados.motivo ?? undefined}
    >
      <p className="text-body-sm text-fg">{texto}</p>
      {final !== null ? (
        <p className="mt-1 text-caption text-fg-muted" data-testid="orientacoes-final">
          {te.final
            .replace('{versao}', String(final.versao))
            .replace('{data}', formatarDataHora(final.criada_em))
            .replace('{situacao}', te.situacaoDoFinal[final.situacao])}
        </p>
      ) : null}
    </div>
  );
}

export function OrientacoesDoCasoPage(): JSX.Element {
  const { id: caseId = '' } = useParams<{ id: string }>();
  const client = useApiClient();
  const [dados, setDados] = useState<OrientacoesDoCasoData | null>(null);
  const [falha, setFalha] = useState<MotivoDaFalha | null>(null);
  const [liberando, setLiberando] = useState(false);

  const carregar = useCallback(async (): Promise<void> => {
    try {
      setDados(await lerOrientacoesDoCaso(client, caseId));
      setFalha(null);
    } catch (err) {
      setFalha(motivoDe(err));
    }
  }, [client, caseId]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  async function liberar(): Promise<void> {
    setLiberando(true);
    try {
      setDados(await liberarAsOrientacoes(client, caseId));
      setFalha(null);
    } catch (err) {
      setFalha(motivoDe(err));
    } finally {
      setLiberando(false);
    }
  }

  const componivel = dados !== null && !['nao_aplicavel', 'indisponivel'].includes(dados.situacao);
  return (
    <section
      className="mx-auto flex max-w-5xl flex-col gap-sc-6 p-sc-6"
      data-testid="orientacoes-equipe-page"
    >
      <header>
        <p className="text-caption uppercase tracking-[0.1em] text-fg-muted">
          {t.sobretituloEquipe}
        </p>
        <h1 className="text-display-sm text-fg">{te.titulo}</h1>
        <p className="mt-1 max-w-prose text-body-sm text-fg-muted">{te.subtitulo}</p>
        <nav className="mt-sc-2 flex flex-wrap gap-sc-4 text-body-sm">
          <Link className="text-accent-700" to={`/casos/${caseId}/resultado`}>
            {te.voltar}
          </Link>
          {/* E159.F3 (GUS-153): a conferência das orientações, com os apontamentos. */}
          <Link
            className="text-accent-700"
            to={`/casos/${caseId}/saidas`}
            data-testid="orientacoes-saidas"
          >
            {ptBR.conferenciaDasSaidas.linkParaAConferencia}
          </Link>
        </nav>
      </header>
      <ProximoPasso client={client} caseId={caseId} />
      {falha !== null ? (
        <div role="alert" className="space-y-sc-2" data-testid="orientacoes-equipe-erro">
          <p className="text-body-sm text-danger-700">{te.erroDaAcao[falha]}</p>
          <Button type="button" variant="secondary" size="sm" onClick={() => void carregar()}>
            {t.tentarDeNovo}
          </Button>
        </div>
      ) : null}
      {dados === null && falha === null ? (
        <p className="text-body-sm text-fg-muted">{t.carregando}</p>
      ) : null}
      {dados !== null ? <Situacao dados={dados} /> : null}
      {dados?.situacao === 'nao_liberada' ? (
        <Button
          className="self-start"
          disabled={liberando}
          onClick={() => void liberar()}
          data-testid="liberar-de-novo"
        >
          {liberando ? te.liberando : te.liberarDeNovo}
        </Button>
      ) : null}
      {dados?.orientacoes ? <OrientacoesReservadas orientacoes={dados.orientacoes} /> : null}
      {componivel ? (
        <BaixarOrientacoes
          rotulo={te.baixarMinuta}
          baixar={() => baixarMinutaDasOrientacoes(client, caseId)}
          testId="baixar-minuta"
        />
      ) : null}
    </section>
  );
}
